import type { FileMetadata } from '../transfer/protocol';

/**
 * IndexedDB persistence for resume support (Feature 8).
 *
 * The receiver checkpoints its progress so that, if the connection drops or the
 * tab reloads, it can re-open the partial file and ask the sender to retransmit
 * only the missing chunks. We persist the FileSystemFileHandle itself (it is
 * structured-cloneable) so a streamed-to-disk download can be resumed in place.
 */
export interface ReceiverCheckpoint {
  roomId: string;
  meta: FileMetadata;
  /** Number of leading, contiguous chunks already durably written. */
  receivedContiguous: number;
  /** Streaming target on disk, when the File System Access API was used. */
  fileHandle?: FileSystemFileHandleLike;
  /** True when buffered in memory (cannot be resumed across a reload). */
  inMemory: boolean;
  updatedAt: number;
}

const DB_NAME = 'beam-transfers';
const DB_VERSION = 1;
const STORE = 'checkpoints';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'roomId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = run(transaction.objectStore(STORE));
        transaction.oncomplete = () => {
          db.close();
          resolve(request.result);
        };
        transaction.onerror = () => {
          db.close();
          reject(transaction.error ?? new Error('IndexedDB transaction failed'));
        };
      }),
  );
}

export async function saveCheckpoint(cp: ReceiverCheckpoint): Promise<void> {
  await tx('readwrite', (store) => store.put({ ...cp, updatedAt: Date.now() }));
}

export async function loadCheckpoint(roomId: string): Promise<ReceiverCheckpoint | undefined> {
  return tx<ReceiverCheckpoint | undefined>('readonly', (store) => store.get(roomId));
}

export async function deleteCheckpoint(roomId: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(roomId));
}

/** Best-effort availability check (private mode / disabled storage). */
export async function isIdbAvailable(): Promise<boolean> {
  try {
    const db = await openDb();
    db.close();
    return true;
  } catch {
    return false;
  }
}
